import React, { SyntheticEvent, useCallback, useState } from 'react';

import { dataLayerProvider, tracker } from '@femtasy/helpers';
import classNames from 'classnames';
import { MessageDescriptor, useIntl } from 'react-intl';

import { CurrentlyPlaying } from 'components/ConnectedAudioPlayer/hls-helper';
import Drawer from 'components/Drawer';
import IconFemtasy from 'components/IconFemtasy';
import Message from 'components/Message';
import PlayButton from 'components/PlayButton';
import Typography from 'components/Typography';

import { isProduction } from 'helpers/env';

import { useAuth } from 'hooks/useAuth';
import { usePlayer } from 'hooks/usePlayer';
import { useTracking } from 'hooks/useTracking';

import CourseTotalDuration from '../CourseTotalDuration';

import CourseFullPlayer from './FullPlayer';
import './style.scss';

const trackingName = (
  currentlyPlaying: CurrentlyPlaying | undefined,
  activeSubscription: boolean,
) => {
  if (!activeSubscription) {
    return 'mini_player_shopping_cart';
  } else if (currentlyPlaying?.type === 'speaker') {
    return 'mini_player_speaker';
  } else {
    return 'mini_player_standard';
  }
};

const trackingPiece = (currentlyPlaying: CurrentlyPlaying | undefined) => {
  return currentlyPlaying
    ? `${currentlyPlaying.type}_${currentlyPlaying.id}`
    : '';
};

const titleForCurrentlyPlaying = (
  current: CurrentlyPlaying,
  formatMessage: (
    descriptor: MessageDescriptor,
    values?: Record<string, string>,
  ) => string,
): string | undefined => {
  const teaser = formatMessage({ id: 'components.MiniPlayer.title' });

  if (current.type === 'speaker') return teaser;
  if (current.type === 'other') return current.otherTitle;

  if (current.audioType === 'audio') {
    return current.storyTitle;
  } else {
    return `${teaser} - ${current.storyTitle}`;
  }
};

const ProgressBar = () => {
  const { mediaDuration, currentTime, fullAudioPlayed } = usePlayer();

  let progress = 0;

  if (mediaDuration) {
    progress = fullAudioPlayed
      ? 100
      : Math.round((currentTime / mediaDuration) * 100);
  }

  return (
    <div className="MiniPlayer__ProgressBar__wrapper">
      <div
        style={{
          width: `${progress}%`,
        }}
        className="MiniPlayer__ProgressBar"
      />
    </div>
  );
};

const Labels = () => {
  const { currentlyPlaying } = usePlayer();
  const { formatMessage } = useIntl();

  return (
    <div className="MiniPlayer__Labels">
      {currentlyPlaying && (
        <>
          <Typography variant="h4" className="MiniPlayer__Title">
            {titleForCurrentlyPlaying(currentlyPlaying, formatMessage)}
          </Typography>
          <Typography variant="small-thick" className="MiniPlayer__Subtitle">
            {currentlyPlaying.speakerName}
          </Typography>
        </>
      )}
    </div>
  );
};

const OpenFullPlayer = ({ onClick }) => {
  const { currentlyPlaying, isPlayingSeries } = usePlayer();
  const { hasPremiumAccess } = useAuth();
  const { trackContentInteraction } = useTracking();

  const clickHandler = React.useCallback(() => {
    onClick();
    trackContentInteraction(
      'click',
      'mini_player_expander_arrow',
      trackingName(currentlyPlaying, hasPremiumAccess),
    );
  }, [onClick, trackContentInteraction, currentlyPlaying, hasPremiumAccess]);

  if (isPlayingSeries) {
    return (
      <div className="MiniPlayer__openPlayer" onClick={clickHandler}>
        <IconFemtasy icon="Arrowup" size="32" />
      </div>
    );
  }

  return null;
};

const CloseCoursePlayer = ({ onClick }: { onClick: () => void }) => {
  const { fullAudioPlayed, currentlyPlaying } = usePlayer();
  const { trackContentInteraction } = useTracking();

  const clickHandler = React.useCallback(() => {
    onClick();
    trackContentInteraction(
      'click',
      'mini_player_close_button',
      trackingPiece(currentlyPlaying),
    );
  }, [currentlyPlaying, onClick, trackContentInteraction]);

  if (!fullAudioPlayed) return null;

  return (
    <div className="MiniPlayer__close" onClick={clickHandler}>
      <IconFemtasy icon="Cancel" size="32" />
    </div>
  );
};

type IProps = {
  fixedToScreenBottom?: boolean;
  interactive?: boolean;
};

const CoursePlayer = ({ fixedToScreenBottom, interactive = true }: IProps) => {
  const {
    isPlaying,
    isLoading,
    currentlyPlaying,
    fullAudioPlayed,
    isPlayingSeries,
  } = usePlayer();

  const { togglePlay, stopPlayback } = usePlayer();

  const { trackContentInteraction } = useTracking();
  const { hasPremiumAccess } = useAuth();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const handleOpenDrawer = useCallback(() => setDrawerOpen(true), []);
  const handleCloseDrawer = useCallback(() => setDrawerOpen(false), []);

  const onClick = React.useCallback(() => {
    if (currentlyPlaying?.type === 'series' && interactive) {
      trackContentInteraction(
        'click',
        trackingName(currentlyPlaying, hasPremiumAccess),
        trackingPiece(currentlyPlaying),
      );

      tracker.sendEvent(
        'Miniplayer',
        [
          {
            action: 'open_course_details',
            audioName: currentlyPlaying.storyTitle,
            pageUrl: window.location.href,
          },
        ],
        !isProduction(),
        [dataLayerProvider],
      );
    }
  }, [
    hasPremiumAccess,
    interactive,
    currentlyPlaying,
    trackContentInteraction,
  ]);

  const onPlayButtonClick = React.useCallback(
    (e: SyntheticEvent) => {
      e.stopPropagation();

      if (currentlyPlaying) {
        togglePlay();

        trackContentInteraction(
          isPlaying ? 'pause' : 'continue',
          trackingName(currentlyPlaying, hasPremiumAccess),
          trackingPiece(currentlyPlaying),
          currentlyPlaying.sourceUrl,
        );
      }
    },
    [
      hasPremiumAccess,
      currentlyPlaying,
      isPlaying,
      togglePlay,
      trackContentInteraction,
    ],
  );

  if (isPlayingSeries) {
    return (
      <>
        <div
          data-testid="course-player"
          className={classNames('CoursePlayer', { fixedToScreenBottom })}
          onClick={onClick}
        >
          <div className="CoursePlayer__content">
            <ProgressBar />
            <div className="CoursePlayer__PlayButton">
              <PlayButton
                size="40"
                isPlaying={isPlaying}
                isLoading={isLoading}
                alreadyListened={fullAudioPlayed}
                onClick={onPlayButtonClick}
              />
            </div>
            <Labels />
            <div className="CoursePlayer__Controls">
              <OpenFullPlayer onClick={handleOpenDrawer} />
              <CloseCoursePlayer onClick={stopPlayback} />
            </div>
          </div>
        </div>
        <Drawer
          header={currentlyPlaying?.storyTitle}
          headerVariant="h3"
          anchor="bottom"
          open={drawerOpen}
          onClose={handleCloseDrawer}
          icon="Arrowdown"
        >
          {/* These values need to be hardcoded atm becase we dont
          have any way to access that information from the player */}
          <CourseTotalDuration totalCourseLength="1:18:45" numberOfAudios={5} />
          {currentlyPlaying?.description && (
            <Typography variant="body">
              <Message message={currentlyPlaying?.description} />
            </Typography>
          )}
          <CourseFullPlayer />
        </Drawer>
      </>
    );
  }

  return null;
};

export default CoursePlayer;
